import { IPhotoModel } from '@common/models';
import { ImageResizerUtil } from '@common/utils';
import classNames from 'classnames';
import { toNumber } from 'lodash';
import { observer } from 'mobx-react';
import React, { useCallback, useMemo } from 'react';
import { Image } from 'react-bootstrap';
import { PhotoUtils } from './photoUtils';
import { getSpeciesIcon } from '@common/utils/imageUtils';

export interface IPetPhotoProps {
  photo?: IPhotoModel;
  petName: string;
  speciesName?: string;
  speciesPlaceHolderOverride: string;
  makeCircularWhenPhoto?: boolean;
  petId?: number;
  enablePetPhotoUpload?: boolean;
  savePetPhoto?: (petId: number | undefined, photoToSave: File) => void;
  outlineColor?:
    | 'red'
    | 'green'
    | 'orange'
    | 'petco-green'
    | 'petco-orange'
    | undefined;
}

export const PetPhoto = observer(
  ({
    photo,
    speciesName,
    speciesPlaceHolderOverride,
    petName,
    height,
    width,
    className,
    makeCircularWhenPhoto = false,
    petId,
    enablePetPhotoUpload,
    savePetPhoto,
    outlineColor,
    ...attrs
  }: IPetPhotoProps & React.ImgHTMLAttributes<HTMLImageElement>) => {
    const speciesIcon = getSpeciesIcon(speciesName);
    const speciesPlaceHolderImage: string | undefined = useMemo(
      () => speciesIcon,
      [speciesIcon],
    );
    const overrideHeight = height != undefined;
    const overrideWidth = width != undefined;
    const lHeight = toNumber(height ? height : 150);
    const lWidth = toNumber(width ? width : 150);
    const photoUrl = useMemo(
      () =>
        ImageResizerUtil.getAzureImageUrlByPhoto(photo, {
          mode: 'crop',
          height: lHeight,
          width: lWidth,
        }),
      [photo, lHeight, lWidth],
    );
    const imageSize = PhotoUtils.getImageSize(
      width,
      height,
      speciesPlaceHolderImage,
      photo,
    );
    const addCircularClass =
      makeCircularWhenPhoto &&
      photo &&
      photo.photoUrl !== '' &&
      !_.isNil(photo.photoUrl);
    const imageClassName = classNames(className, {
      'circle-img': addCircularClass,
      [`pet-photo-outline-${outlineColor}`]: !!outlineColor,
    });

    const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);

    const handleImageUploadClick = useCallback(
      async (event: any) => {
        if (event.type == 'click') {
          if (hiddenFileInput?.current) {
            hiddenFileInput.current.click();
            event.stopPropagation();
          }
        }
        if (event.type == 'change') {
          if (hiddenFileInput?.current?.value) {
            let files = hiddenFileInput?.current?.files;
            const file = files && files?.length! > 0 ? files[0] : null;
            const resizeResult = await ImageResizerUtil.ResizeImageWithPreview(
              file!,
              {
                saveMaxWidth: 640,
                saveMaxHeight: 640,
                previewMaxWidth: 200,
                previewMaxHeight: 200,
              },
            );
            savePetPhoto?.(petId!, resizeResult.fileToSave!);
          }
        }
      },
      [petId, savePetPhoto],
    );

    if (enablePetPhotoUpload && (photoUrl || speciesPlaceHolderImage)) {
      return (
        <>
          <input
            ref={hiddenFileInput}
            name={'petImg'}
            capture="user"
            multiple
            type="file"
            accept="image/png, image/jpeg, image/jpg"
            hidden={true}
            onChange={handleImageUploadClick}
          />
          <Image
            className={imageClassName}
            src={photoUrl ? photoUrl : speciesPlaceHolderImage}
            alt={`photo of pet ${petName}`}
            height={overrideHeight ? lHeight : imageSize.height}
            width={overrideWidth ? lWidth : imageSize.width}
            onClick={handleImageUploadClick}
            {...attrs}
          />
        </>
      );
    } else {
      return (
        <>
          {(photoUrl || speciesPlaceHolderImage) && (
            <Image
              className={imageClassName}
              src={photoUrl ? photoUrl : speciesPlaceHolderImage}
              alt={`photo of pet ${petName}`}
              height={overrideHeight ? lHeight : imageSize.height}
              width={overrideWidth ? lWidth : imageSize.width}
              {...attrs}
            />
          )}
        </>
      );
    }
  },
);
